import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useCurrentUser } from "../../lib/auth/useAuth";
import { useAccountRegion } from "../../lib/api/useAccountRegion";
import { SIM_ACTIVE, SIM_DEMO } from "./flags";
import { getSimContext, setSimContext } from "./context";
import type { SimContextState } from "./types";

/**
 * Normal builds only: keeps the kit's `context.market` / `context.region` equal
 * to the signed-in account's real market and city (GET /api/account/region), so
 * `isFeatureEnabled("seasonal")` is true only for a PH account and the season
 * cards ask for the right city. Signed out → the main market.
 *
 * In demo mode it renders nothing: there the Sim Panel controls the market.
 * This only decides what the client renders — the server enforces the market
 * on its own (seasonal.routes answers 404 to every main-market account).
 * Mounted once, in App.tsx.
 */
export function MarketSync() {
  if (SIM_DEMO || !SIM_ACTIVE) return null;
  return <MarketSyncInner />;
}

const SEASONAL_KEY = ["seasonal"] as const;

function MarketSyncInner() {
  const { data: user } = useCurrentUser();
  const signedIn = Boolean(user);
  const { data: region } = useAccountRegion(signedIn);
  const queryClient = useQueryClient();

  useEffect(() => {
    const current = getSimContext();
    const next: Partial<SimContextState> =
      signedIn && region
        ? { market: region.market, region: region.phRegion ?? current.region }
        : { market: "main" };
    if (current.market === next.market && current.region === (next.region ?? current.region)) return;
    setSimContext(next);
    // The cards are written for the city: never show one city's cards under another's.
    void queryClient.invalidateQueries({ queryKey: SEASONAL_KEY });
  }, [signedIn, region, queryClient]);

  return null;
}
